import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { from, throwError, EMPTY } from 'rxjs';
import { switchMap, catchError, finalize } from 'rxjs/operators';
import { LoaderService } from '@core/services/loader.service';
import { SessionService } from '@core/services/session.service';
import { environment } from 'environments/environment';

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn) => {
    const msalService = inject(MsalService);
    const loader = inject(LoaderService);
    const sessionService = inject(SessionService);

    if (sessionService.sessionExpired$()) {
        return EMPTY;
    }

    const activeAccount = msalService.instance.getActiveAccount();
    if (!activeAccount) {
        console.warn('No active MSAL account found; redirecting to login');
        sessionService.handleSessionTimeout();
        return EMPTY;
    }

    loader.show();
    return from(
        msalService.instance.acquireTokenSilent({
            account: activeAccount,
            scopes: environment.APP_SCOPES,
        })
    ).pipe(
        // Any failure to silently acquire a token (expired session, revoked
        // consent, network hiccup during renewal, etc.) means the request
        // cannot go out authenticated, so it always routes through the
        // centralized session timeout handler rather than only reacting to
        // one specific MSAL error type.
        catchError(error => {
            console.warn('Silent token acquisition failed; ending session', error);
            sessionService.handleSessionTimeout();
            return EMPTY;
        }),
        switchMap(tokenResponse => next(req.clone({
            setHeaders: {
                Authorization: `Bearer ${tokenResponse.accessToken}`,
                'X-EVE-ORIGINATOR': 'null',
                'X-EVE-CORRELATIONID': 'null'
            }
        })).pipe(
            catchError(httpError => {
                if (httpError instanceof HttpErrorResponse && httpError.status === 401) {
                    console.warn('Received 401 Unauthorized; session likely expired');
                    sessionService.handleSessionTimeout();
                    return EMPTY;
                }
                return throwError(() => httpError);
            })
        )),
        finalize(() => {
            loader.hide();
        })
    );
};
